import React, { lazy, Suspense } from "react";
import CookieConsent, { Cookies } from "react-cookie-consent";
import {
    BrowserRouter as Router, Route, Switch, Redirect
} from "react-router-dom";
import { CircularProgress, Box } from '@material-ui/core';
import HomeComponent from "../components/home";
import NavigationComponent from '../components/navigation';
import Footer from "../components/footer";
import Breadcrumb from "../components/common/Breadcrumb";
import ScrollToTop from "../components/common/ScrollToTop";
import { RedirectConnectorsPair, RedirectSlugToPair } from "../components/integrations/IntegrationRedirects";

// Lazy load components for better performance
const DemoComponent = lazy(() => import("../components/demo"));
const ForgotPasswordComponent = lazy(() => import("../components/forgot"));
const InvitePage = lazy(() => import("../components/invite/InvitePage"));
const ChangeSystemPassword = lazy(() => import("../components/change-system-password"));
const SourceSetupComponent = lazy(() => import("../components/home/SourceSetup"));
const LegalComponent = lazy(() => import("../components/legal"));
const LoginComponent = lazy(() => import('../components/login'));
const PricingPage = lazy(() => import("../components/pricing/PricingPage"));
const PrivacyComponent = lazy(() => import("../components/privacy"));
const CookiePolicyComponent = lazy(() => import("../components/cookie-policy"));
const MasterSubscriptionAgreementComponent = lazy(() => import("../components/master-subscription-agreement"));
const PlatformPage = lazy(() => import("../components/platform/PlatformPage"));
const ResetPasswordComponent = lazy(() => import("../components/reset-password"));
const SignupComponent = lazy(() => import("../components/sign-up"));
const RegisterCompanyIndex = lazy(() => import("../components/sign-up/register-company-index"));
const TermsComponent = lazy(() => import("../components/terms"));
const UnsubscribeEmail = lazy(() => import("../components/unsubscribe/UnsubscribeEmail"));
const EmailInformation = lazy(() => import("../components/email-info/EmailInformation"));
const NotFoundPage = lazy(() => import("../components/not-found/NotFoundPage"));
const ConnectorsPage = lazy(() => import("../components/integrations/ConnectorsPage"));
const ConnectorDetailsPage = lazy(() => import("../components/integrations/ConnectorDetailsPage"));
const SourceToDestinationPage = lazy(() => import("../components/integrations/SourceToDestinationPage"));
const SourceToDestinationsPage = lazy(() => import("../components/integrations/SourceToDestinationsPage"));
const ToDestinationPage = lazy(() => import("../components/integrations/ToDestinationPage"));
const SourceVariationPage = lazy(() => import("../components/integrations/SourceVariationPage"));
const GuidePage = lazy(() => import("../components/integrations/GuidePage"));
const ComparePage = lazy(() => import("../components/compare/ComparePage"));
const AlternativesPage = lazy(() => import("../components/compare/AlternativesPage"));
const BestPage = lazy(() => import("../components/compare/BestPage"));
const ApiPage = lazy(() => import("../components/compare/ApiPage"));
const DataModelsPage = lazy(() => import("../components/compare/DataModelsPage"));
const UseCasePage = lazy(() => import("../components/compare/UseCasePage"));
const ProblemPage = lazy(() => import("../components/compare/ProblemPage"));
const HowToConnectSalesforce = lazy(() => import("../components/how-to/HowToConnectSalesforce"));

// Additional components for Resources dropdown
const ConnectorDocumentation = lazy(() => import("../components/documentation/ConnectorDocumentation"));
const DocumentationPage = lazy(() => import("../components/documentation"));
const DocumentationLanding = lazy(() => import("../components/documentation/DocumentationLanding"));
const PowerBIConnectionGuide = lazy(() => import("../components/documentation/PowerBIConnectionGuide"));
const TableauConnectionGuide = lazy(() => import("../components/documentation/TableauConnectionGuide"));
const QlikSenseConnectionGuide = lazy(() => import("../components/documentation/QlikSenseConnectionGuide"));
const QlikViewConnectionGuide = lazy(() => import("../components/documentation/QlikViewConnectionGuide"));
const ConnectorsSourcesPage = lazy(() => import("../components/documentation/ConnectorsSourcesPage"));
const ConnectorsDestinationsPage = lazy(() => import("../components/documentation/ConnectorsDestinationsPage"));
const AccountManagementPage = lazy(() => import("../components/documentation/AccountManagementPage"));
const FAQsPage = lazy(() => import("../components/documentation/FAQsPage"));
const HowToGuidesPage = lazy(() => import("../components/documentation/HowToGuidesPage"));
const SecurityCompliance = lazy(() => import("../components/security/SecurityCompliance"));
const SupportPage = lazy(() => import("../components/support"));
const ProfessionalServices = lazy(() => import("../components/support/ProfessionalServices"));
const SystemStatus = lazy(() => import("../components/status/SystemStatus"));
const BlogInsights = lazy(() => import("../components/blog/BlogInsights"));
const ArticlePage = lazy(() => import("../components/blog/ArticlePage"));
const DataDeletionPage = lazy(() => import("../components/data-deletion"));
const ReleasesPage = lazy(() => import("../components/releases"));
const ExtensionAttributionPage = lazy(() => import("../components/vsix/ExtensionAttributionPage"));
const SMBSolutionPage = lazy(() => import("../components/solutions/SMBSolutionPage"));
const ShopifyToQuickbooksPage = lazy(() => import("../components/solutions/ShopifyToQuickbooksPage"));
const ReportingPage = lazy(() => import("../components/solutions/ReportingPage"));
const AgencyOwnerReportingPage = lazy(() => import("../components/solutions/AgencyOwnerReportingPage"));
const SolutionLandingPage = lazy(() => import("../components/solutions/SolutionLandingPage"));
const SolutionsHubPage = lazy(() => import("../components/solutions/SolutionsHubPage"));
const UseCasesHubPage = lazy(() => import("../components/useCases/UseCasesHubPage"));
const UseCaseDetailPage = lazy(() => import("../components/useCases/UseCaseDetailPage"));
const { REACT_APP_MARKETING_SITE } = process.env;


// Loading component for lazy routes
const LoadingFallback = () => (
    <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: '200px' }}>
        <CircularProgress size={40} />
    </Box>
);

export default function RouterComponent() {
    return (<>
        {/* Navigation for the app */}
        <div className="layout-wrapper">
        <Router>
            <ScrollToTop />
            <NavigationComponent />
            <div className="layout-main-and-footer">
                <div className="layout-main dc-site-shell">
                    <Breadcrumb />
                    <div className="layout-content">
                        <Switch>
                <Route exact path="/">
                    <HomeComponent />
                </Route>
                <Route exact path="/shopify-to-quickbooks">
                    <Suspense fallback={<LoadingFallback />}>
                        <ShopifyToQuickbooksPage />
                    </Suspense>
                </Route>
                <Route exact path="/reporting">
                    <Suspense fallback={<LoadingFallback />}>
                        <ReportingPage />
                    </Suspense>
                </Route>
                <Route exact path="/solutions">
                    <Suspense fallback={<LoadingFallback />}>
                        <SolutionsHubPage />
                    </Suspense>
                </Route>
                <Route exact path="/solutions/:solutionSlug">
                    <Suspense fallback={<LoadingFallback />}>
                        <SolutionLandingPage />
                    </Suspense>
                </Route>
                <Route exact path="/use-cases">
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCasesHubPage />
                    </Suspense>
                </Route>
                <Route exact path="/use-case/:useCaseSlug">
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCaseDetailPage />
                    </Suspense>
                </Route>
                <Route exact path="/agency-reporting-automation">
                    <Suspense fallback={<LoadingFallback />}>
                        <AgencyOwnerReportingPage />
                    </Suspense>
                </Route>
                <Route exact path="/marketing-dashboard-automation">
                    <Suspense fallback={<LoadingFallback />}>
                        <SMBSolutionPage />
                    </Suspense>
                </Route>
                <Route exact path="/automated-reporting">
                    <Suspense fallback={<LoadingFallback />}>
                        <SMBSolutionPage />
                    </Suspense>
                </Route>
                <Route exact path="/orders-to-sheets">
                    <Suspense fallback={<LoadingFallback />}>
                        <SMBSolutionPage />
                    </Suspense>
                </Route>
                <Route exact path="/sync-business-tools">
                    <Suspense fallback={<LoadingFallback />}>
                        <SMBSolutionPage />
                    </Suspense>
                </Route>
                <Route exact path="/crm-data-sync">
                    <Suspense fallback={<LoadingFallback />}>
                        <SMBSolutionPage />
                    </Suspense>
                </Route>
                <Route exact path="/ads-to-dashboard">
                    <Suspense fallback={<LoadingFallback />}>
                        <SMBSolutionPage />
                    </Suspense>
                </Route>
                <Route exact path="/reporting-data-automation">
                    <Suspense fallback={<LoadingFallback />}>
                        <SMBSolutionPage />
                    </Suspense>
                </Route>
                <Route exact path="/connectors">
                    <Suspense fallback={<LoadingFallback />}>
                        <ConnectorsPage />
                    </Suspense>
                </Route>
                <Route exact path="/integrations">
                    <Suspense fallback={<LoadingFallback />}>
                        <ConnectorsPage />
                    </Suspense>
                </Route>
                <Route exact path="/integrations/:sourceSlug/to/:destinationSlug">
                    <Suspense fallback={<LoadingFallback />}>
                        <SourceToDestinationPage />
                    </Suspense>
                </Route>
                <Route exact path="/integrations/:sourceSlug/to">
                    <Suspense fallback={<LoadingFallback />}>
                        <SourceToDestinationsPage />
                    </Suspense>
                </Route>
                <Route exact path="/integrations/to/:destinationSlug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ToDestinationPage />
                    </Suspense>
                </Route>
                <Route exact path="/sources/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <SourceVariationPage />
                    </Suspense>
                </Route>
                <Route exact path="/guides/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <GuidePage />
                    </Suspense>
                </Route>
                <Route exact path="/compare/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ComparePage />
                    </Suspense>
                </Route>
                <Route exact path="/alternatives/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <AlternativesPage />
                    </Suspense>
                </Route>
                <Route exact path="/best/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <BestPage />
                    </Suspense>
                </Route>
                <Route exact path="/api/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ApiPage />
                    </Suspense>
                </Route>
                <Route exact path="/data-models/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <DataModelsPage />
                    </Suspense>
                </Route>
                <Route exact path="/analytics/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCasePage />
                    </Suspense>
                </Route>
                <Route exact path="/dashboards/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCasePage />
                    </Suspense>
                </Route>
                <Route exact path="/reporting/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCasePage />
                    </Suspense>
                </Route>
                <Route exact path="/automate/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ProblemPage />
                    </Suspense>
                </Route>
                <Route exact path="/incremental/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ProblemPage />
                    </Suspense>
                </Route>
                <Route exact path="/cdc/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ProblemPage />
                    </Suspense>
                </Route>
                <Route exact path="/replicate/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ProblemPage />
                    </Suspense>
                </Route>
                <Route exact path="/connectors/:sourceSlug/:destinationSlug">
                    <RedirectConnectorsPair />
                </Route>
                <Route exact path="/sync/:sourceToDestSlug">
                    <RedirectSlugToPair />
                </Route>
                <Route exact path="/export/:sourceToDestSlug">
                    <RedirectSlugToPair />
                </Route>
                <Route exact path="/integrations/:connectorName">
                    <Suspense fallback={<LoadingFallback />}>
                        <ConnectorDetailsPage />
                    </Suspense>
                </Route>
                <Route exact path="/how-to-connect-salesforce">
                    <Suspense fallback={<LoadingFallback />}>
                        <HowToConnectSalesforce />
                    </Suspense>
                </Route>
                <Route exact path="/documentation">
                    <Suspense fallback={<LoadingFallback />}>
                        <DocumentationPage />
                    </Suspense>
                </Route>
                {/* <Route exact path="/documentation/getting-started">
                    <Suspense fallback={<LoadingFallback />}>
                        <GettingStartedPage />
                    </Suspense>
                </Route> */}
                <Route exact path="/documentation/connectors">
                    <Suspense fallback={<LoadingFallback />}>
                        <DocumentationLanding />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/connectors/sources">
                    <Suspense fallback={<LoadingFallback />}>
                        <ConnectorsSourcesPage />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/connectors/destinations">
                    <Suspense fallback={<LoadingFallback />}>
                        <ConnectorsDestinationsPage />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/account-management">
                    <Suspense fallback={<LoadingFallback />}>
                        <AccountManagementPage />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/faqs">
                    <Suspense fallback={<LoadingFallback />}>
                        <FAQsPage />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/how-to-guides">
                    <Suspense fallback={<LoadingFallback />}>
                        <HowToGuidesPage />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/power-bi-connection-guide">
                    <Suspense fallback={<LoadingFallback />}>
                        <PowerBIConnectionGuide />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/tableau-connection-guide">
                    <Suspense fallback={<LoadingFallback />}>
                        <TableauConnectionGuide />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/qlik-sense-connection-guide">
                    <Suspense fallback={<LoadingFallback />}>
                        <QlikSenseConnectionGuide />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/qlikview-connection-guide">
                    <Suspense fallback={<LoadingFallback />}>
                        <QlikViewConnectionGuide />
                    </Suspense>
                </Route>
                <Route exact path="/documentation/:connectorId">
                    <Suspense fallback={<LoadingFallback />}>
                        <ConnectorDocumentation />
                    </Suspense>
                </Route>
                <Route exact path="/source-connector/:name">
                    <Suspense fallback={<LoadingFallback />}>
                        <SourceSetupComponent />
                    </Suspense>
                </Route>
                <Route exact path="/destination-connector/:name">
                    <Suspense fallback={<LoadingFallback />}>
                        <SourceSetupComponent />
                    </Suspense>
                </Route>
                {/* <Route exact path="/salesforce-connector">
                    <SalesforceConnector />
                </Route>
                <Route exact path="/quickbooks-connector">
                    <QuickbooksConnector />
                </Route> */}
                <Route exact path="/login">
                    <Suspense fallback={<LoadingFallback />}>
                        <LoginComponent />
                    </Suspense>
                </Route>
                <Route exact path="/invite">
                    <Suspense fallback={<LoadingFallback />}>
                        <InvitePage />
                    </Suspense>
                </Route>
                <Route
                    exact
                    path="/login-setup"
                    render={({ location }) => (
                        <Redirect to={{ pathname: '/invite', search: location.search }} />
                    )}
                />
                <Route exact path="/forgot">
                    <Suspense fallback={<LoadingFallback />}>
                        <ForgotPasswordComponent />
                    </Suspense>
                </Route>
                <Route exact path="/reset">
                    <Suspense fallback={<LoadingFallback />}>
                        <ResetPasswordComponent />
                    </Suspense>
                </Route>
                <Route exact path="/change-password">
                    <Suspense fallback={<LoadingFallback />}>
                        <ChangeSystemPassword />
                    </Suspense>
                </Route>
                <Route exact path="/platform">
                    <Suspense fallback={<LoadingFallback />}>
                        <PlatformPage />
                    </Suspense>
                </Route>
                <Route exact path="/pricing">
                    <Suspense fallback={<LoadingFallback />}>
                        <PricingPage />
                    </Suspense>
                </Route>
                <Route exact path="/company">
                    <Redirect to="/" />
                </Route>
                <Route exact path="/demo">
                    <Suspense fallback={<LoadingFallback />}>
                        <DemoComponent />
                    </Suspense>
                </Route>
                <Route exact path="/register">
                    <Suspense fallback={<LoadingFallback />}>
                        <SignupComponent />
                    </Suspense>
                </Route>
                <Route exact path="/register-company">
                    <Suspense fallback={<LoadingFallback />}>
                        <RegisterCompanyIndex />
                    </Suspense>
                </Route>
                <Route exact path="/legal">
                    <Suspense fallback={<LoadingFallback />}>
                        <LegalComponent />
                    </Suspense>
                </Route>
                <Route exact path="/terms">
                    <Suspense fallback={<LoadingFallback />}>
                        <TermsComponent />
                    </Suspense>
                </Route>
                <Route exact path="/privacy">
                    <Suspense fallback={<LoadingFallback />}>
                        <PrivacyComponent />
                    </Suspense>
                </Route>
                <Route exact path="/cookie-policy">
                    <Suspense fallback={<LoadingFallback />}>
                        <CookiePolicyComponent />
                    </Suspense>
                </Route>
                <Route exact path="/master-subscription-agreement">
                    <Suspense fallback={<LoadingFallback />}>
                        <MasterSubscriptionAgreementComponent />
                    </Suspense>
                </Route>
                <Route exact path="/security">
                    <Suspense fallback={<LoadingFallback />}>
                        <SecurityCompliance />
                    </Suspense>
                </Route>
                <Route exact path="/support">
                    <Suspense fallback={<LoadingFallback />}>
                        <SupportPage />
                    </Suspense>
                </Route>
                <Route exact path="/professional-services">
                    <Suspense fallback={<LoadingFallback />}>
                        <ProfessionalServices />
                    </Suspense>
                </Route>
                <Route exact path="/system-status">
                    <Suspense fallback={<LoadingFallback />}>
                        <SystemStatus />
                    </Suspense>
                </Route>
                <Route exact path="/releases">
                    <Suspense fallback={<LoadingFallback />}>
                        <ReleasesPage />
                    </Suspense>
                </Route>
                <Route exact path="/vsix/extension">
                    <Suspense fallback={<LoadingFallback />}>
                        <ExtensionAttributionPage />
                    </Suspense>
                </Route>
                <Route exact path="/blog">
                    <Suspense fallback={<LoadingFallback />}>
                        <BlogInsights />
                    </Suspense>
                </Route>
                <Route exact path="/blog/:slug">
                    <Suspense fallback={<LoadingFallback />}>
                        <ArticlePage />
                    </Suspense>
                </Route>
                <Route exact path="/data-deletion">
                    <Suspense fallback={<LoadingFallback />}>
                        <DataDeletionPage />
                    </Suspense>
                </Route>
                <Route exact path="/unsubscribe">
                    <Suspense fallback={<LoadingFallback />}>
                        <UnsubscribeEmail />
                    </Suspense>
                </Route>
                <Route exact path="/emails/info">
                    <Suspense fallback={<LoadingFallback />}>
                        <EmailInformation />
                    </Suspense>
                </Route>
                <Route exact path="/emails">
                    <Redirect to="/emails/info" />
                </Route>
                <Route>
                    <Suspense fallback={<LoadingFallback />}>
                        <NotFoundPage />
                    </Suspense>
                </Route>
                    </Switch>
                </div>
            </div>
            <Footer />
            </div>
        </Router>
        </div>
        {/* cookie */}
        <CookieConsent
            location="bottom"
            buttonText="Ok, Got it"
            disableStyles
            flipButtons
            cookieName="dataconnectorcookie"
            cookieValue={true}
            sameSite="strict"
            style={{
                alignItems: "baseline",
                background: "var(--data-con-blue)",
                color: "white",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                left: "0px",
                position: "fixed",
                width: "100%",
                zIndex: "999",
                bottom: "0px",
                padding: "10px 0"
            }}
            buttonStyle={{ color: "#fff", fontSize: "13px", fontWeight: 500, background: "var(--orange)", border: "none", padding: "5px 10px", borderRadius: "5px", margin: "0 5px 0 10px" }}
            expires={365}
            enableDeclineButton
            declineButtonStyle={{ color: "var(--orange)", fontSize: "13px", fontWeight: 500, background: "#fff", border: "1px solid var(--orange)", padding: "5px 10px", borderRadius: "5px", marginLeft: "5px" }}
            declineButtonText="Cookie Policy"
            onDecline={() => {
                Cookies.set("dataconnectorcookie", false, { expires: new Date(Date.now() - 1) })
                window.location.href = `${REACT_APP_MARKETING_SITE}/cookie-policy`;
            }}
        >
            Data Connector uses cookies to ensure you get the best experience on our website.
        </CookieConsent>
    </>)
}
